import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { get } from "@rails/request.js";
import { Modal, useModal } from "./Modal";

const ProgressBar = ({ progressPercentage }) => {
  let color = 'bg-sspurple-alt'
  if (progressPercentage > 10 && progressPercentage <= 30) {
    color = 'bg-gradient-to-r from-sspurple-alt to-sspurple'
  } else if (progressPercentage > 30 && progressPercentage <= 50) {
    color = 'bg-gradient-to-r from-sspurple-alt via-sspurple to-ssblue'
  } else if (progressPercentage > 50 && progressPercentage <= 70) {
    color = 'bg-gradient-to-r from-sspurple via-ssblue to-ssblue-alt'
  } else if (progressPercentage > 70 && progressPercentage <= 90) {
    color = 'bg-gradient-to-r from-ssblue via-ssblue-alt to-ssgreen'
  } else if (progressPercentage > 90) {
    color = 'bg-gradient-to-r from-ssblue via-ssgreen to-ssgreen-alt'
  }
  return (
    <div className='h-2 w-full bg-gray-300'>
      <div style={{ width: `${progressPercentage}%` }} className={`h-full ${color}`}> </div>
    </div>
  )
}

const canRecord = typeof window !== 'undefined'
  && typeof window.MediaRecorder !== 'undefined'
  && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ]
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || ''
}

const extForMime = (mime) => {
  if (!mime) return 'webm'
  if (mime.includes('mp4')) return 'mp4'
  return 'webm'
}

const formatTime = (s) => {
  const m = Math.floor(s / 60)
  const sec = (s % 60).toString().padStart(2, '0')
  return `${m}:${sec}`
}

export const VideoUploader = ({ initialUrl, setUrl }) => {
  const [uploading, setUploading] = useState(false)
  const videoRef = useRef(null)
  const livePreviewRef = useRef(null)
  const [progressPercentage, setProgressPercentage] = useState(0.0)
  const [file, setFile] = useState(null)
  const { modalOpen, setModalOpen } = useModal()

  const [mode, setMode] = useState(canRecord ? 'record' : 'upload')
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [recordError, setRecordError] = useState(null)
  const [facingMode, setFacingMode] = useState('user')
  const [streamReady, setStreamReady] = useState(false)
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])
  const streamRef = useRef(null)
  const timerRef = useRef(null)

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: 'video/*', maxFiles: 1, maxSize: 1000000000 })

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setStreamReady(false)
  }

  const reset = () => {
    setFile(null)
    setProgressPercentage(0)
    setUploading(false)
    setElapsed(0)
    setRecordError(null)
    if (videoRef.current) videoRef.current.src = initialUrl || ''
  }

  const startCamera = async () => {
    setRecordError(null)
    stopStream()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: true,
      })
      streamRef.current = stream
      if (livePreviewRef.current) {
        livePreviewRef.current.srcObject = stream
      }
      setStreamReady(true)
    } catch (err) {
      setRecordError(
        err && err.name === 'NotAllowedError'
          ? 'Camera/microphone access was denied. Please allow access and try again.'
          : 'Could not start the camera. Check that your device has a camera and try again.'
      )
    }
  }

  const flipCamera = () => {
    setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))
  }

  useEffect(() => {
    if (mode !== 'record' || file || !modalOpen) return
    startCamera()
  }, [mode, facingMode, modalOpen, file])

  const startRecording = () => {
    if (!streamRef.current) return
    setRecordError(null)
    const mimeType = pickMimeType()
    const mr = mimeType ? new MediaRecorder(streamRef.current, { mimeType }) : new MediaRecorder(streamRef.current)
    mediaRecorderRef.current = mr
    chunksRef.current = []

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
    }
    mr.onstop = () => {
      const type = mr.mimeType || mimeType || 'video/webm'
      const blob = new Blob(chunksRef.current, { type })
      const ext = extForMime(type)
      const recordedFile = new File([blob], `recording-${Date.now()}.${ext}`, { type })
      setFile(recordedFile)
      stopStream()
    }

    mr.start()
    setRecording(true)
    setElapsed(0)
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000)
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setRecording(false)
  }

  const switchMode = (newMode) => {
    if (recording) stopRecording()
    stopStream()
    reset()
    setMode(newMode)
  }

  useEffect(() => {
    if (!modalOpen) stopStream()
  }, [modalOpen])

  useEffect(() => () => stopStream(), [])

  const uploadFile = async () => {
    setUploading(true)
    const key = `video-${file.name}`
    const response = await get(`/api/v1/uploads/r2_hash?key=${key}`, {
      responseKind: "application/json",
    })

    if (response.ok) {
      const json = await response.json
      const xhr = new XMLHttpRequest()
      await new Promise((resolve) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            setProgressPercentage((event.loaded / event.total) * 100)
          }
        })
        xhr.addEventListener("loadend", () => {
          resolve(xhr.readyState === 4 && xhr.status === 200)
          setUploading(false)
          setUrl(json.base_url + '/' + key)
          setModalOpen(false)
        })
        xhr.open("PUT", json.signed_url, true)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
      })
    } else {
      console.log('refresh the page. your session is expired')
      setUploading(false)
    }
  }

  useEffect(() => {
    if (!file) return
    if (!file.type.startsWith('video')) {
      setRecordError('That file is not recognized as video. Try MP4, MOV, or WEBM.')
      return
    }
    if (videoRef.current) videoRef.current.src = URL.createObjectURL(file)
  }, [file])

  const TabButton = ({ value, label }) => (
    <button
      onClick={() => switchMode(value)}
      className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
        mode === value ? 'bg-ssblue text-white' : 'text-slate-700 hover:bg-slate-100'
      }`}
    >
      {label}
    </button>
  )

  return <>
    <video ref={videoRef} className={`h-96 w-96 mx-auto object-cover rounded-md cursor-pointer shadow-lg border border-gray-400 border-dashed border-2 ${!initialUrl && !file ? 'hidden' : ''}`} src={initialUrl} onClick={() => setModalOpen(true)} />
    {!file && !initialUrl && (
      <button
        className="relative block mt-4 sm:mx-auto w-lg rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        onClick={() => setModalOpen(true)}
      >
        <img src="https://images.shosay.com/icon-message-speech.png" className="mx-auto h-16 w-16" />
        <span className="mt-2 block text-sm font-semibold text-gray-900">Record or upload video</span>
      </button>
    )}
    {modalOpen && (
      <Modal maxWidth="max-w-2xl" modalOpen={modalOpen} setModalOpen={setModalOpen} icon="video" title="Add video">
        <div className="relative p-2 sm:p-8">
          <div className="sm:text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Add Video</h2>
          </div>

          {canRecord && !file && (
            <div className="mt-4 flex gap-1 bg-white rounded-lg p-1 border-2 border-black">
              <TabButton value="record" label="Record" />
              <TabButton value="upload" label="Upload" />
            </div>
          )}

          <div className="w-full mx-auto mt-4 sm:mt-6">
            {mode === 'upload' && !file && (
              <div
                className="bg-gray-50 mt-3 relative block min-h-20 w-full border-2 border-gray-300 flex flex-col justify-center cursor-pointer border-dashed rounded-lg p-6 sm:p-12 text-center hover:border-gray-400"
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Click or drop a <b>video</b> file to upload.
                </span>
              </div>
            )}

            {mode === 'record' && !file && (
              <div className="mt-2 flex flex-col items-center gap-3">
                <div className="relative w-full bg-black rounded-lg overflow-hidden border-2 border-black aspect-video">
                  <video
                    ref={livePreviewRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {recording && (
                    <div className="absolute top-2 left-2 flex items-center gap-2 bg-black/60 text-white px-2 py-1 rounded-md text-sm font-mono">
                      <span className="h-2 w-2 rounded-full bg-ssred animate-pulse" />
                      {formatTime(elapsed)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {streamReady && !recording && (
                    <button
                      onClick={flipCamera}
                      className="h-12 w-12 rounded-full bg-white border-2 border-black flex items-center justify-center hover:bg-slate-100"
                      aria-label="Switch camera"
                      type="button"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                        <path d="M23 4v6h-6" />
                        <path d="M1 20v-6h6" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                        <path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14" />
                      </svg>
                    </button>
                  )}
                  {!recording ? (
                    <button
                      onClick={startRecording}
                      disabled={!streamReady}
                      className="h-16 w-16 rounded-full bg-ssred border-2 border-black flex items-center justify-center text-white shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Start recording"
                      type="button"
                    >
                      <div className="h-6 w-6 rounded-full bg-white" />
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="h-16 w-16 rounded-full bg-ssred border-2 border-black flex items-center justify-center text-white shadow-lg animate-pulse"
                      aria-label="Stop recording"
                      type="button"
                    >
                      <div className="h-5 w-5 bg-white rounded-sm" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-slate-600">
                  {recording ? 'Tap to stop' : streamReady ? 'Tap to start recording' : 'Waiting for camera…'}
                </div>
              </div>
            )}

            {recordError && (
              <div className="mt-3 text-sm text-ssred">{recordError}</div>
            )}

            {file && (
              <div className="bg-gray-50 w-full mt-2 border-2 rounded-lg border-gray-300 p-2">
                <video ref={videoRef} controls className="w-full rounded-md border-2 border-black" src={initialUrl} />
                <div className="mt-2 text-sm text-gray-500 break-words">
                  {file.name} - {(file.size / 1000 / 1000).toFixed(2)} MB
                </div>
                {uploading && <ProgressBar progressPercentage={progressPercentage} />}
                <div className="mt-2 flex justify-center gap-x-3">
                  <button
                    onClick={reset}
                    className="mt-2 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-md bg-ssbeige px-3 py-2 text-slate-900 border-2 border-black"
                  >
                    <span className="absolute h-0 w-0 rounded-full bg-ssbeige-alt transition-all duration-300 group-hover:h-56 group-hover:w-32"></span>
                    <span className="relative">{mode === 'record' ? 'Re-record' : 'Reset'}</span>
                  </button>
                  <button
                    onClick={uploadFile}
                    className="mt-2 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-md bg-ssblue px-3 py-2 text-white border-2 border-black"
                  >
                    <span className="absolute h-0 w-0 rounded-full bg-sspurple transition-all duration-300 group-hover:h-56 group-hover:w-32"></span>
                    <span className="relative">Save</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    )}
  </>
}
